import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Button from 'components/Button/Button';
import FormItem from 'components/FormItem/FormItem';
import IconButton from 'components/IconButton/IconButton';
import Input from 'components/Input/Input';

import ArrowBack24 from 'icons/ArrowBack24';

import useApi from 'utils/api';
import { useAuth } from 'utils/auth';
import { validateEmail } from 'utils/validate';

import styles from './auth.scss';

/**
 * User login screen. Consists of two steps: Email and Password.
 */
export default function Login() {
    const auth = useAuth();
    const api = useApi();
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState(false);

    const { t } = useTranslation();

    // Login form step: "email" or "password" entering.
    const [step, setStep] = React.useState('email');
    const [email, setEmail] = React.useState({ value: '', error: null });
    const [password, setPassword] = React.useState({ value: '', error: null });

    const stepLayout = {
        email: {
            title: t('welcome.login.step.email.title'),
            caption: t('welcome.login.step.email.description'),
            icon: (
                <svg width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M18 3C9.72 3 3 9.72 3 18c0 8.28 6.72 15 15 15h7.5v-3H18c-6.51 0-12-5.49-12-12S11.49 6 18 6s12 5.49 12 12v2.145c0 1.185-1.065 2.355-2.25 2.355s-2.25-1.17-2.25-2.355V18c0-4.14-3.36-7.5-7.5-7.5-4.14 0-7.5 3.36-7.5 7.5 0 4.14 3.36 7.5 7.5 7.5 2.07 0 3.96-.84 5.31-2.205.975 1.335 2.655 2.205 4.44 2.205 2.955 0 5.25-2.4 5.25-5.355V18c0-8.28-6.72-15-15-15Zm0 19.5c-2.49 0-4.5-2.01-4.5-4.5s2.01-4.5 4.5-4.5 4.5 2.01 4.5 4.5-2.01 4.5-4.5 4.5Z"
                        fill="#A4ABC1"
                    />
                </svg>
            ),
        },
        password: {
            title: t('welcome.login.step.password.title'),
            caption: `${t('welcome.login.step.password.description')} ${email.value}`,
            icon: (
                <svg width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M3 25.5h30v3H3v-3Zm1.725-6.075L6 17.205l1.275 2.22 1.95-1.125-1.275-2.22h2.55v-2.25H7.95l1.275-2.205-1.95-1.125L6 12.705 4.725 10.5l-1.95 1.125L4.05 13.83H1.5v2.25h2.55L2.775 18.3l1.95 1.125Zm10.05-1.125 1.95 1.125L18 17.205l1.275 2.22 1.95-1.125-1.275-2.22h2.55v-2.25h-2.55l1.275-2.205-1.95-1.125L18 12.705 16.725 10.5l-1.95 1.125 1.275 2.205H13.5v2.25h2.55l-1.275 2.22ZM34.5 13.83h-2.55l1.275-2.205-1.95-1.125L30 12.705 28.725 10.5l-1.95 1.125 1.275 2.205H25.5v2.25h2.55l-1.275 2.22 1.95 1.125L30 17.205l1.275 2.22 1.95-1.125-1.275-2.22h2.55v-2.25Z"
                        fill="#A4ABC1"
                    />
                </svg>
            ),
        },
        resetPassword: {
            title: t('welcome.login.step.resetPassword.title'),
            caption: `${t('welcome.login.step.resetPassword.description')}`,
        },
    };

    /**
     * Saves email password to state and reset errors.
     * @param {event} e Event triggered on email input.
     */
    function handleEmailChange(e) {
        const value = e.target.value.replace(/\s/gi, '');
        const error = null;
        setEmail((email) => ({ ...email, value, error }));
    }

    /**
     * Validate Email and go to the next step.
     * @param {event} e KeyDown event.
     */
    function handleEmailKeyDown(e) {
        if (e.which === 13) {
            handleContinueClick();
        }
    }

    /**
     * Navigates user to the password enter step.
     */
    function handleContinueClick() {
        const error = validateEmail(email.value);
        setEmail((email) => ({ ...email, error }));

        if (error === null) {
            setStep('password');
        }
    }

    /**
     * Validates password entered by user.
     * @param {event} e Event triggered on password value changes
     */
    function handlePasswordChange(e) {
        const value = e.target.value.replace(/\s/gi, '');
        setPassword((password) => ({ ...password, value, error: null }));
    }

    /**
     * Start login proccess if Enter down in the password input.
     * @param {Event} e Event key pressed.
     */
    function handlePasswordKeyDown(e) {
        if (e.which === 13) {
            handleLoginClick();
        }
    }

    function handleLoginFailure(error) {
        setPassword((password) => ({ ...password, error }));
    }

    /**
     * Try to authorize user and redirect to workspace.
     */
    function handleLoginClick() {
        const credentials = {
            username: email.value,
            password: password.value,
        };
        setLoading(true);
        auth.signin(credentials)
            .then((result) => {
                setLoading(false);
            })
            .catch((error) => {
                if (error instanceof TypeError) {
                    handleLoginFailure(new Error('Не удалось связаться с сервером авторизации.'));
                } else {
                    handleLoginFailure(new Error('Ошибка авторизации: неправильная почта или пароль.'));
                }
                setTimeout(() => setLoading(false), 500);
                console.error(error);
            });
    }

    function handleForgotPasswordClick() {
        setStep('resetPassword');

        api.sendResetPasswordEmail(email.value)
            .then((response) => {})
            .catch((error) => console.error(error));
    }

    /**
     * Go back to email form.
     */
    function handleBackClick() {
        if (step === 'email') {
            navigate('/');
        } else if (step === 'resetPassword') {
            setStep('password');
        } else {
            setStep('email');
        }
    }

    return (
        <div className={styles.sign}>
            <div className={styles.top + ' ' + styles.flexStart}>
                <div className={styles.back}>
                    <IconButton icon={<ArrowBack24 />} onClick={handleBackClick} />
                </div>
                <h2>{stepLayout[step].title}</h2>
                <p className={styles.text + ' ' + styles.center}>{stepLayout[step].caption}</p>
                <div className={styles.authForm}>
                    {step === 'email' && (
                        <FormItem style={{ padding: 0 }} error={email.error}>
                            <Input
                                onKeyDown={handleEmailKeyDown}
                                placeholder={t('welcome.login.step.email.placeholder')}
                                value={email.value}
                                type={'text'}
                                name={'email'}
                                onChange={handleEmailChange}
                            />
                        </FormItem>
                    )}
                    {step === 'password' && (
                        <FormItem style={{ padding: 0 }} error={password.error}>
                            <Input
                                autoFocus={true}
                                onKeyDown={handlePasswordKeyDown}
                                placeholder={'Пароль'}
                                value={password.value}
                                name={'password'}
                                type={'password'}
                                onChange={handlePasswordChange}
                            />
                        </FormItem>
                    )}
                </div>
            </div>
            <div className={styles.bottom}>
                {step === 'email' && (
                    <Button
                        text={t('welcome.login.step.email.action')}
                        fill={true}
                        disabled={email.error !== null || email.value === ''}
                        onClick={handleContinueClick}
                    />
                )}
                {step === 'password' && (
                    <>
                        <Button
                            text={t('welcome.main.signIn')}
                            loading={loading}
                            fill={true}
                            disabled={email.error !== null || password.value === ''}
                            onClick={handleLoginClick}
                        />
                        <Button
                            text={t('welcome.login.step.password.forgotPassword')}
                            fill={true}
                            level={'tertiary'}
                            onClick={handleForgotPasswordClick}
                        />
                    </>
                )}
                {step === 'resetPassword' && (
                    <Button
                        level={'secondary'}
                        fill={true}
                        text={t('welcome.login.step.resetPassword.back')}
                        onClick={() => {
                            setStep('email');
                        }}
                    />
                )}
            </div>
        </div>
    );
}
